import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Founding Testers Program — public landing page (/founding-testers).
 * Self-contained design tokens per mockup: Space Grotesk + Inter,
 * #0f1117 bg, blue accent, amber scarcity badge, signal-green benefits.
 */
const C = {
  bg: "#0f1117", raised: "#151823", card: "#1a1e2a", border: "#262b3a", borderSoft: "#1f2330",
  accent: "#4a9eff", accentDim: "#4a9eff1a", amber: "#f5a623", amberDim: "#f5a62314",
  signal: "#3ddc97", signalDim: "#3ddc971a", text: "#e6e8ee", dim: "#8b93a7", faint: "#565d72",
};
const display = "'Space Grotesk', Inter, sans-serif";

const YOU_GET = [
  "Locked-in founding pricing on your plan — 50% off, for as long as you stay subscribed",
  "Early access to every new module, before general release",
  "A direct line to us — your feedback genuinely shapes the roadmap",
  'A "Founding Tester" badge on your account (public credit if you want it, private if you don\'t)',
  "First look at pricing/tier changes before they roll out broadly",
];
const WE_ASK = [
  "Actually use it — a few sessions a month, not a one-time signup",
  "Tell us what's broken, confusing, or missing — rough edges are expected at this stage",
  "A short monthly check-in (2 minutes, not a meeting) on what's working and what isn't",
  "Openness to being asked for a testimonial or case study later — never required, always optional",
];
const STEPS = [
  { t: "Pick your plan", p: "Sign up for whichever tier fits your station — Free through Network." },
  { t: "Founding code applied", p: "Your 50% founding discount and badge attach automatically at checkout." },
  { t: "Use it, break it, tell us", p: "Use the platform for real. Send feedback anytime from the Contact page." },
  { t: "Keep your rate, always", p: "Your founding pricing stays locked in for as long as you're subscribed." },
];
const FAQ = [
  { q: "Does founding pricing ever expire?",
    a: "No — as long as your subscription stays active without a lapse, your founding rate stays locked in, even as our regular prices change over time." },
  { q: "What if I need to pause or cancel for a bit?",
    a: "Founding pricing is tied to continuous subscription. If you cancel and come back later, you'd rejoin at whatever the current regular pricing is at that time." },
  { q: "Do I have to give public feedback or testimonials?",
    a: "Never required. We'll sometimes ask if you'd be open to one, but it's always optional, and private feedback is just as valuable to us." },
  { q: "What happens when the 50 seats are gone?",
    a: "This page closes to new signups, and everyone after that gets the standard free trial instead. Founding seats won't reopen once this cohort is full." },
];

export default function FoundingTesters() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get("/founding/status").then((r) => setStatus(r.data)).catch(() => setStatus({ open: false, seats_total: 50, seats_claimed: 50, seats_left: 0 }));
  }, []);

  const claim = async () => {
    if (!status?.open) return;
    if (!user) {
      localStorage.setItem("founding_intent", "1");
      navigate("/login");
      return;
    }
    setClaiming(true);
    try {
      const { data } = await api.post("/founding/claim");
      toast.success(data.already
        ? `You're already Founding Tester #${data.cohort_number}`
        : `Seat claimed — you're Founding Tester #${data.cohort_number}! 🎉`);
      navigate("/dashboard/billing?tab=plan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim a seat.");
      api.get("/founding/status").then((r) => setStatus(r.data)).catch(() => {});
    } finally { setClaiming(false); }
  };

  const open = status?.open;
  const left = status?.seats_left ?? "—";

  const Btn = ({ children, ...props }) => (
    <button {...props}
      style={{ background: C.accent, color: "#08101f", border: "none", borderRadius: 9,
               padding: "13px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer",
               fontFamily: "Inter, sans-serif", display: "inline-flex", alignItems: "center", gap: 8,
               opacity: claiming ? 0.7 : 1 }}>
      {children}
    </button>
  );

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "Inter, system-ui, sans-serif", minHeight: "100vh", lineHeight: 1.5 }}
         data-testid="founding-testers-page">
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 32px" }}>

        {/* Hero */}
        <div style={{ padding: "72px 0 48px", textAlign: "center" }}>
          <div data-testid="founding-seat-badge"
               style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.amberDim,
                        color: C.amber, border: "1px solid #f5a62340", padding: "7px 16px",
                        borderRadius: 999, fontSize: 12, fontWeight: 700, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber }} />
            {status ? (open
              ? `${status.seats_claimed} of ${status.seats_total} founding seats claimed`
              : "Founding seats are full — program closed") : "Loading seats…"}
          </div>
          <h1 style={{ fontFamily: display, fontWeight: 700, fontSize: 42, lineHeight: 1.15, marginBottom: 16 }}>
            {open === false
              ? <>The Founding Tester cohort is full</>
              : <>Help shape BroadcastFlow AI —<br />as a Founding Tester</>}
          </h1>
          <p style={{ fontSize: 16, color: C.dim, maxWidth: 560, margin: "0 auto 28px" }}>
            {open === false
              ? "All 50 founding seats have been claimed. You can still sign up for the standard free trial — founding seats won't reopen for this cohort."
              : "We're opening a limited number of spots to stations who want in early: locked-in pricing, direct input on what we build next, and first access to every new module — before anyone else sees it."}
          </p>
          {open === false ? (
            <Btn onClick={() => navigate(user ? "/dashboard" : "/login")} data-testid="founding-cta-closed">
              Start a standard free trial →
            </Btn>
          ) : (
            <Btn onClick={claim} disabled={claiming} data-testid="founding-cta-hero">
              {claiming ? "Claiming…" : "Claim a founding seat →"}
            </Btn>
          )}
          <div style={{ fontSize: 12.5, color: C.faint, marginTop: 14 }}>
            Limited to the first {status?.seats_total ?? 50} stations. Closes when seats run out, or Aug 31 — whichever comes first.
          </div>
        </div>

        {/* The Deal */}
        <Section tag="The Deal" title="What you get, what we ask">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
            <DealCard icon="🎁" iconBg={C.signalDim} iconColor={C.signal} title="You get"
                      items={YOU_GET} mark="✓" markColor={C.signal} testid="founding-you-get" />
            <DealCard icon="🤝" iconBg={C.accentDim} iconColor={C.accent} title="We ask"
                      items={WE_ASK} mark="→" markColor={C.accent} testid="founding-we-ask" />
          </div>
        </Section>

        {/* How it works */}
        <Section tag="How It Works" title="Four steps, no surprises">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 18 }}>
            {STEPS.map((s, i) => (
              <div key={s.t} style={{ textAlign: "center" }} data-testid={`founding-step-${i + 1}`}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accentDim, color: C.accent,
                              fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                              margin: "0 auto 12px", fontSize: 13 }}>{i + 1}</div>
                <h4 style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>{s.t}</h4>
                <p style={{ fontSize: 12, color: C.dim }}>{s.p}</p>
              </div>
            ))}
          </div>
          <div data-testid="founding-tier-note"
               style={{ background: C.raised, border: `1px solid ${C.border}`, borderRadius: 12,
                        padding: "18px 22px", fontSize: 13, color: C.dim, lineHeight: 1.6,
                        marginTop: 32, textAlign: "center" }}>
            Founding Tester status is a <b style={{ color: C.text }}>badge and discount on your existing plan</b> — not
            a separate plan of its own. Pick any tier (Free, Starter, Professional, Pro Radio, or Network); founding
            perks apply on top, and you can still upgrade or downgrade normally at any time.
          </div>
        </Section>

        {/* FAQ */}
        <Section tag="Questions" title="Before you sign up">
          <div style={{ maxWidth: 680, margin: "0 auto" }} data-testid="founding-faq">
            {FAQ.map((f, i) => (
              <div key={f.q} style={{ borderBottom: `1px solid ${C.borderSoft}`, padding: i === 0 ? "0 0 16px" : "16px 0" }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>{f.q}</div>
                <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6 }}>{f.a}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Final CTA */}
        <div style={{ textAlign: "center", padding: "56px 0 64px" }}>
          <h2 style={{ fontFamily: display, fontSize: 26, marginBottom: 14 }} data-testid="founding-final-count">
            {open === false
              ? "This cohort is closed — but the platform isn't."
              : `Only ${left} seats left. After that, this offer's gone.`}
          </h2>
          {open === false ? (
            <Btn onClick={() => navigate(user ? "/dashboard" : "/login")} data-testid="founding-cta-final-closed">
              Start a standard free trial →
            </Btn>
          ) : (
            <Btn onClick={claim} disabled={claiming} data-testid="founding-cta-final">
              {claiming ? "Claiming…" : "Claim a founding seat →"}
            </Btn>
          )}
        </div>

      </div>
    </div>
  );
}

function Section({ tag, title, children }) {
  return (
    <div style={{ padding: "48px 0", borderTop: `1px solid ${C.borderSoft}` }}>
      <div style={{ fontFamily: display, fontSize: 11, fontWeight: 600, letterSpacing: ".12em",
                    textTransform: "uppercase", color: C.faint, textAlign: "center", marginBottom: 10 }}>{tag}</div>
      <h2 style={{ fontFamily: display, fontSize: 24, fontWeight: 600, textAlign: "center", marginBottom: 36 }}>{title}</h2>
      {children}
    </div>
  );
}

function DealCard({ icon, iconBg, iconColor, title, items, mark, markColor, testid }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 26 }} data-testid={testid}>
      <div style={{ width: 36, height: 36, borderRadius: 9, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 16, marginBottom: 14, background: iconBg, color: iconColor }}>
        {icon}
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{title}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {items.map((t) => (
          <div key={t} style={{ fontSize: 13.5, color: C.dim, display: "flex", gap: 9, alignItems: "flex-start" }}>
            <span style={{ flexShrink: 0, marginTop: 1, color: markColor }}>{mark}</span>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}
